import type { Market } from "@config/site/types";
import { getPagePathByLocale, shoppingBagPage } from "@lib/constants/pagePaths";
import { useStore } from "@store/store";
import { useIsomorphicLayoutEffect } from "@ecco/ui/use-isomorphic-layout-effect";
import { useRouter } from "@i18n/routing";
import { useLocale } from "next-intl";
import { useMemo } from "react";

const DEFAULT_LIMIT = 10;

type Config = {
    shouldRedirect?: boolean;
};

export const useOrderItemsLimit = (country: string, config?: Config) => {
    const cart = useStore((state) => state.data.me.cartInfo.cart);
    const { push } = useRouter();
    const locale = useLocale();

    const exceptionLimits: Partial<Record<Market, number>> = {
        // Define only exceptions — rest will fallback to DEFAULT_LIMIT
    };

    const getItemsLimit = (country: string) =>
        exceptionLimits[country.toLowerCase() as Market] ?? DEFAULT_LIMIT;

    const itemsLimit = getItemsLimit(country);

    const redirectBackToBag = () => {
        push(`${getPagePathByLocale(shoppingBagPage, locale)}`);
    };

    const itemsLimitReached = useMemo(() => {
        const itemsCount = cart?.lineItems?.reduce((total, item) => total + item.quantity, 0) ?? 0;
        return itemsCount > itemsLimit;
    }, [cart?.lineItems, itemsLimit]);

    useIsomorphicLayoutEffect(() => {
        if (!config?.shouldRedirect || !itemsLimitReached) return;
        redirectBackToBag();
    }, [itemsLimitReached]);

    return {
        itemsLimit,
        itemsLimitReached,
        redirectBackToBag,
    };
};
